import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  SHOWROOM_CHARGE_TYPE_OPTIONS,
  computeShowroomChargeAmount,
  emptyShowroomCharge,
  isShowroomPercentType,
  type ProductDealType,
  type ProductProfitRuleType,
  type ShowroomChargeDraft,
  type ShowroomChargeType,
} from "@/lib/catalog/dealPartners";
import type { PartyOption } from "@/lib/parties/partiesStore";
import { pkrAmountShortLabel } from "@/lib/formatPkrAmount";
import { cn } from "@/lib/utils";
import { BadgePercent } from "lucide-react";
import { useEffect } from "react";

type Props = {
  value: ShowroomChargeDraft | null | undefined;
  onChange: (next: ShowroomChargeDraft) => void;
  /** Parties with is_company=1 and type=both for the current company. */
  companyParties: PartyOption[];
  /** @deprecated Prefer commissionBase */
  basePurchase?: number | null;
  /** Amount used to preview percentage/fixed commission (sale price, net profit, or purchase). */
  commissionBase?: number | null;
  dealType?: ProductDealType | null;
  profitRuleType?: ProductProfitRuleType | null;
  /** Owner Vehicle + Owner Surplus: Profit % 100% (read-only type/value). */
  locked?: boolean;
};

/** Showroom Charges — company party picker + type/value. */
export function CompanyOwnerCommissionSection({
  value,
  onChange,
  companyParties,
  basePurchase = null,
  commissionBase = null,
  dealType = null,
  profitRuleType = null,
  locked = false,
}: Props) {
  const row = value ?? emptyShowroomCharge();
  const previewBase = commissionBase ?? basePurchase;
  const isPercent = isShowroomPercentType(row.commissionType);
  const showProfitValue = row.commissionType === "fixed" || isPercent;
  const computedAmount = computeShowroomChargeAmount(previewBase, row);
  const profitShort =
    row.commissionType === "fixed" && row.commissionValue != null
      ? pkrAmountShortLabel(row.commissionValue)
      : isPercent && computedAmount > 0
        ? pkrAmountShortLabel(computedAmount)
        : "";

  // Default charge type to the first option.
  useEffect(() => {
    if (locked || row.commissionType != null) return;
    onChange(
      emptyShowroomCharge({
        ...row,
        commissionType: SHOWROOM_CHARGE_TYPE_OPTIONS[0].value,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locked, row.commissionType]);

  // Auto-select the first company party when none is set.
  useEffect(() => {
    if (row.partyId || companyParties.length === 0) return;
    const first = companyParties[0];
    onChange(
      emptyShowroomCharge({
        ...row,
        partyId: first.id,
        partyName: first.name,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyParties, row.partyId]);

  const patch = (next: Partial<ShowroomChargeDraft>) => {
    if (locked && (next.commissionType !== undefined || next.commissionValue !== undefined)) {
      return;
    }
    const selectedParty =
      next.partyId !== undefined
        ? companyParties.find((p) => String(p.id) === String(next.partyId))
        : companyParties.find((p) => String(p.id) === String(row.partyId));
    const merged = emptyShowroomCharge({
      partyId: next.partyId !== undefined ? next.partyId : row.partyId,
      partyName:
        next.partyName !== undefined
          ? next.partyName
          : selectedParty?.name ?? row.partyName,
      commissionType: next.commissionType !== undefined ? next.commissionType : row.commissionType,
      commissionValue: next.commissionValue !== undefined ? next.commissionValue : row.commissionValue,
      commissionAmount: next.commissionAmount !== undefined ? next.commissionAmount : row.commissionAmount,
    });
    const amount = computeShowroomChargeAmount(previewBase, merged);
    onChange({
      ...merged,
      commissionAmount: amount > 0 ? amount : null,
    });
  };

  return (
    <div className="rounded-xl border border-border/50 bg-card/40 p-4 space-y-4">
      <div className="flex items-start gap-2">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <BadgePercent className="h-4 w-4" />
        </div>
        <div>
          <p className="text-sm font-semibold text-foreground">Showroom Charges</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {locked
              ? "Owner Surplus: 100% of profit above Owner Demand Price belongs to the Showroom."
              : dealType === "owner_vehicle" && profitRuleType === "commission"
                ? "Commission for your company. Sale % uses Expected Sale Price; Profit % uses net profit."
                : dealType === "partnership"
                  ? "Independent of Deal Partner Profit % (Fixed, Sale %, or Profit %). Does not change partner shares."
                  : "Commission for your company. Saved when a commission value is entered."}
          </p>
        </div>
      </div>

      <div className="rounded-lg border border-border/40 bg-background/70 p-3">
        <div className="flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-end">
          <div className="min-w-0 w-full space-y-1.5 sm:w-auto sm:min-w-[11rem] sm:flex-1 lg:max-w-[16rem]">
            <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Company</Label>
            <Select
              value={row.partyId ?? undefined}
              onValueChange={(v) => {
                const party = companyParties.find((p) => String(p.id) === String(v));
                patch({ partyId: v, partyName: party?.name ?? null });
              }}
            >
              <SelectTrigger className="glass-input h-10">
                <SelectValue placeholder={companyParties.length ? "Select company" : "No company parties"} />
              </SelectTrigger>
              <SelectContent>
                {companyParties.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="w-full min-w-[18rem] space-y-1.5 sm:w-auto sm:flex-[1.2] sm:basis-[18rem]">
            <div className="flex items-baseline justify-between gap-2">
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">
                Showroom Charges
              </Label>
              {profitShort ? <span className="text-xs font-medium text-primary">{profitShort}</span> : null}
            </div>
            <div className="flex h-10 w-full min-w-[18rem] flex-nowrap items-stretch overflow-hidden rounded-md border border-input bg-background shadow-sm">
              <div className="relative flex min-w-0 flex-1 items-center border-r border-input">
                <Input
                  type="number"
                  className={cn(
                    "h-10 min-w-0 flex-1 rounded-none rounded-l-md border-0 bg-transparent shadow-none focus-visible:ring-0",
                    isPercent ? "pl-3 pr-8" : "px-3",
                    locked || dealType === "purchased" ? "bg-muted/30" : null,
                  )}
                  min={0}
                  max={isPercent ? 100 : undefined}
                  step={isPercent ? "0.01" : "any"}
                  disabled={locked || !showProfitValue || dealType === "purchased"}
                  value={
                    !showProfitValue || row.commissionValue == null
                      ? ""
                      : isPercent
                        ? String(Number(Number(row.commissionValue).toFixed(2)))
                        : String(row.commissionValue)
                  }
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === "") {
                      patch({ commissionValue: null });
                      return;
                    }
                    let n = Number(raw);
                    if (!Number.isFinite(n)) {
                      patch({ commissionValue: null });
                      return;
                    }
                    if (isPercent && n > 100) n = 100;
                    if (n < 0) n = 0;
                    if (isPercent) {
                      n = Number(n.toFixed(2));
                    }
                    patch({ commissionValue: n });
                  }}
                  placeholder={!showProfitValue ? "Select type" : isPercent ? "0–100" : "Amount"}
                />
                {isPercent ? (
                  <span className="pointer-events-none absolute right-2.5 text-xs font-medium text-muted-foreground">
                    %
                  </span>
                ) : null}
              </div>

              <Select
                value={row.commissionType || SHOWROOM_CHARGE_TYPE_OPTIONS[0].value}
                disabled={locked || dealType === "purchased"}
                onValueChange={(v) => {
                  patch({ commissionType: v as ShowroomChargeType, commissionValue: null });
                }}
              >
                <SelectTrigger className="h-10 w-[9.5rem] shrink-0 rounded-none rounded-r-md border-0 bg-transparent shadow-none focus:ring-0">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  {SHOWROOM_CHARGE_TYPE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
